import { Moon, Sun } from "lucide-react";
import { NavLink, Outlet } from "react-router-dom";
import { can, type Capability } from "@snipe/core";
import { useSession } from "@/auth/session";
import { useTheme } from "@/lib/theme";
import { cn } from "@/lib/cn";
import { SnipeMark } from "./brand";
import { Button } from "./ui/button";
import { StatusChip } from "./status";

interface NavItem {
  to: string;
  label: string;
  capability: Capability;
}

const NAV: NavItem[] = [
  { to: "/", label: "Overview", capability: "ops:read" },
  { to: "/identities", label: "Identities", capability: "ops:read" },
  { to: "/runs", label: "Runs", capability: "ops:read" },
  // Only an admin holds `users:manage`, so the link is absent rather than
  // present-and-refusing for anybody else. The API enforces the same
  // capability; this only decides what is worth drawing.
  { to: "/users", label: "Users", capability: "users:manage" },
  { to: "/alerts", label: "Alerts", capability: "alerts:read" },
];

export function AppShell() {
  const { user, signOut } = useSession();
  const { theme, toggle } = useTheme();

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-10 border-b border-border bg-card/95 backdrop-blur">
        <div className="mx-auto flex max-w-[1400px] flex-wrap items-center gap-x-6 gap-y-2 px-4 py-2">
          <span className="flex items-center gap-2 text-sm font-semibold tracking-tight">
            <SnipeMark size={22} />
            Snipe <span className="text-muted-foreground">ops</span>
          </span>

          <nav aria-label="Sections" className="flex items-center gap-1">
            {NAV.filter((item) => user !== null && can(user.role, item.capability)).map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  cn(
                    "rounded-md px-2 py-1 text-sm",
                    isActive
                      ? "bg-accent font-medium text-foreground"
                      : "text-muted-foreground hover:text-foreground",
                  )
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-3">
            {user !== null ? (
              <span className="flex items-center gap-2 text-xs text-muted-foreground">
                <span>{user.email}</span>
                <StatusChip tone="muted">{user.role}</StatusChip>
              </span>
            ) : null}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggle}
              aria-label={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
            >
              {theme === "dark" ? (
                <Sun aria-hidden="true" className="h-4 w-4" />
              ) : (
                <Moon aria-hidden="true" className="h-4 w-4" />
              )}
            </Button>
            <Button variant="outline" size="sm" onClick={() => void signOut()}>
              Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-4 py-5">
        <Outlet />
      </main>
    </div>
  );
}
